import { RenderedArrowsRegistry } from '../private/RenderedArrowsRegistry';
import { EDITED_ARROW_NAME, Panorama } from '../private/PanoramaHandler';
import { Map } from 'immutable';
import he from 'he';
import { ArrowPosition, CameraPosition } from '../../panorama/Positions';
import { isPolygonalArrow, isSimpleArrow, isTeleportArrow, } from '../../panorama/arrows/arrowTypeguards';
import { ArrowType, IndicatorType } from '../../panorama/enums';
import { EMPTY_GUID } from './utils/Guid';
import { FriendlyNameService } from './types/PanoramaArrowIdentifier';
import { renderTeleportArrow } from './types/arrows/renderers/TeleportArrowRenderer';
import { LayerStyle } from './types/LayerStyle';
import { HotspotStyle } from './types/HotspotStyle';
import { isLocatedOnPanoramaActiveView } from './utils/isLocatedOnPanoramaActiveView';
export class PanoramaV2 {
    constructor(_hotspotFactory, _panorama, _activePanorama, ///do all best to be a source of truth what is active
    _allPanoramas, //TODO: reason why is immutable
    _panoramaChangedListeners = new Set(), _onDisposeListeners = new Set()) {
        this._hotspotFactory = _hotspotFactory;
        this._panorama = _panorama;
        this._activePanorama = _activePanorama;
        this._allPanoramas = _allPanoramas;
        this._panoramaChangedListeners = _panoramaChangedListeners;
        this._onDisposeListeners = _onDisposeListeners;
        this._arrows = new RenderedArrowsRegistry();
        this.getKrpanoGlobalObj = () => this._pano.globalObj;
        this.subscribeOnDispose = (listener) => {
            this._onDisposeListeners.add(listener);
            return () => this._onDisposeListeners.delete(listener);
        };
        this.addScenes = async (scenes) => {
            var _a, _b;
            const hasZeroScenes = this._allPanoramas.size === 0;
            this._allPanoramas = this._allPanoramas.withMutations((map) => {
                scenes.forEach((scene) => {
                    map.set(scene.guid, scene.tourScenePanorama);
                });
            });
            // if there was no scene before adding scenes, we want to show the first scene that was added
            if (hasZeroScenes) {
                const tourSceneGuidToLoad = this._allPanoramas.keySeq().first();
                if (tourSceneGuidToLoad) {
                    await this.updateActivePanoramaView((_b = (_a = this._allPanoramas.get(tourSceneGuidToLoad)) === null || _a === void 0 ? void 0 : _a.startingSceneIndex) !== null && _b !== void 0 ? _b : 0, tourSceneGuidToLoad);
                }
            }
        };
        this.updateScene = async (tourSceneGuid, scene) => {
            var _a;
            this._allPanoramas = this._allPanoramas.set(tourSceneGuid, scene);
            if (this._activePanorama.tourSceneGuid === tourSceneGuid) {
                await this.updateActivePanoramaView((_a = scene === null || scene === void 0 ? void 0 : scene.startingSceneIndex) !== null && _a !== void 0 ? _a : 0, tourSceneGuid, true);
            }
        };
        this.removeSceneById = (tourSceneGuid) => {
            this._allPanoramas = this._allPanoramas.delete(tourSceneGuid);
            if (this._activePanorama.tourSceneGuid === tourSceneGuid) {
                // When active scene is removed, first of remainng scenetour is used as fallback
                // If none sceneTours are left, then use empty
                const subPanorama = this._allPanoramas.keySeq().first();
                if (!subPanorama) {
                    this._pano.loadEmptyPano().catch((e) => {
                        throw e;
                    });
                    return;
                }
                const position = new CameraPosition(undefined, undefined, undefined, undefined, subPanorama);
                this.lookToCameraPosition(position, true).catch((e) => {
                    throw e;
                });
            }
        };
        this.changeScene = async (tourSceneGuid, sceneIndex) => {
            await this.updateActivePanoramaView(sceneIndex, tourSceneGuid);
        };
        this.updateActivePanoramaView = async (sceneIndex, tourSceneGuid, forceReload, //e.g. when replacing existing panorama
        panoramaLookTo) => {
            const shouldTourSceneBeLoaded = this._activePanorama.tourSceneGuid !== tourSceneGuid || forceReload;
            if (shouldTourSceneBeLoaded) {
                const newPanoramaToLoad = this._allPanoramas.get(tourSceneGuid);
                // This loads xml and does not trigger onSceneChanged event
                await this.loadPanorama(newPanoramaToLoad);
                this._activePanorama = {
                    ...this._activePanorama,
                    tourSceneGuid: tourSceneGuid,
                };
                // Load scene has to be called as well, since loadPanorama does not load the scene, only loads the XML
                // Triggers onSceneChangedEvent
                this._pano.loadScene(sceneIndex, panoramaLookTo === null || panoramaLookTo === void 0 ? void 0 : panoramaLookTo.xAxis, panoramaLookTo === null || panoramaLookTo === void 0 ? void 0 : panoramaLookTo.yAxis, panoramaLookTo === null || panoramaLookTo === void 0 ? void 0 : panoramaLookTo.fov, 'BLEND(1)');
            }
            else {
                const shouldOnlySceneIndexChangeOnCurrentTourScene = this._activePanorama.sceneIndex !== sceneIndex;
                if (shouldOnlySceneIndexChangeOnCurrentTourScene) {
                    this._pano.loadScene(sceneIndex, panoramaLookTo === null || panoramaLookTo === void 0 ? void 0 : panoramaLookTo.xAxis, panoramaLookTo === null || panoramaLookTo === void 0 ? void 0 : panoramaLookTo.yAxis, panoramaLookTo === null || panoramaLookTo === void 0 ? void 0 : panoramaLookTo.fov, 'BLEND(0.2)');
                }
            }
        };
        this.lookToCameraPosition = async (cameraPosition, lookToquick) => {
            await this.updateActivePanoramaView(cameraPosition.sceneIndex, cameraPosition.tourSceneGuid, false, cameraPosition);
            this._panorama.lookToCameraPosition(cameraPosition, lookToquick);
        };
        this.lookToArrow = async (arrow) => {
            if (isSimpleArrow(arrow) || isTeleportArrow(arrow)) {
                await this.updateActivePanoramaView(arrow.sceneIndex, arrow.tourSceneGuid, false, {
                    xAxis: arrow.arrowPositionConfiguration.arrowPosition.xAxis,
                    yAxis: arrow.arrowPositionConfiguration.arrowPosition.yAxis,
                    sceneIndex: arrow.sceneIndex,
                    fov: arrow.fov,
                });
                this._panorama.lookToSimpleArrow(arrow);
            }
            else if (isPolygonalArrow(arrow)) {
                const polygonCenter = this._panorama.getCenterOfPolygon(arrow.arrowPositionConfiguration.arrowPosition.arrowArea);
                await this.updateActivePanoramaView(arrow.sceneIndex, arrow.tourSceneGuid, false, {
                    xAxis: polygonCenter.xAxis,
                    yAxis: polygonCenter.yAxis,
                    sceneIndex: arrow.sceneIndex,
                    fov: arrow.fov,
                });
                this._pano.lookTo({
                    xAxis: polygonCenter.xAxis,
                    yAxis: polygonCenter.yAxis,
                    fov: arrow.fov,
                    sceneIndex: arrow.sceneIndex,
                });
            }
        };
        this.adjustPanoFov = (fov) => this._pano.setZoom(fov);
        this.synchronizeCameraPositionTo = (sourcePanorama, view) => {
            const currentCameraPosition = sourcePanorama.getCameraPosition();
            const areUsingSameView = currentCameraPosition.sceneIndex === (view === null || view === void 0 ? void 0 : view.sceneIndex) &&
                currentCameraPosition.tourSceneGuid === (view === null || view === void 0 ? void 0 : view.tourSceneGuid);
            if (view === undefined || areUsingSameView) {
                this.lookToCameraPosition(currentCameraPosition, true).catch((e) => {
                    throw e;
                });
            }
        };
        this.isArrowRegistered = (arrowIdentifier) => {
            return Boolean(this._arrows.get(arrowIdentifier));
        };
        this.renderArrowToPano = (arrowToRender, arrowIdentifier, renderOptions, delayedRenderInMs) => {
            var _a;
            const existingArrow = (_a = this._arrows.get(arrowIdentifier)) === null || _a === void 0 ? void 0 : _a.arrow;
            if (existingArrow && this._isDifferentArrowType(arrowToRender, existingArrow)) {
                this.removeArrow(arrowIdentifier);
            }
            if (isSimpleArrow(arrowToRender)) {
                this.renderSinglePointHotspot(arrowToRender, arrowIdentifier, renderOptions, delayedRenderInMs);
            }
            else if (isTeleportArrow(arrowToRender)) {
                renderTeleportArrow(this, arrowToRender, arrowIdentifier, renderOptions);
            }
            else {
                this.renderPolygonalArrow(arrowToRender, arrowIdentifier, renderOptions);
            }
            this._arrows.set(arrowIdentifier, { arrow: arrowToRender, renderOptions });
        };
        this.getPolygonalDrawer = (arrowNameIdentifier) => this._panorama.getPolygonalDrawer(arrowNameIdentifier);
        this.removeArrow = (arrowNameIdentifier) => {
            this._panorama.removeArrow(arrowNameIdentifier);
            this._arrows.delete(arrowNameIdentifier);
        };
        this.removeLayer = (arrowNameIdentifier) => {
            this._panorama.removeLayer(arrowNameIdentifier);
        };
        this.clear = () => {
            const keys = Array.from(this._arrows.asReadonly
                .filter((item) => item.arrow.arrowPositionConfiguration.arrowPositionType !== ArrowType.Teleport)
                .keys());
            keys.forEach((arrowId) => {
                this.removeArrow(arrowId);
            });
        };
        this.getPanoramaInfo = () => {
            return {
                maxFov: this.getMaxFov(),
                minFov: this.getMinFov(),
                sceneIndex: this._activePanorama.sceneIndex,
                panorama: this._activePanorama.tourSceneGuid,
            };
        };
        this.getPosition = () => {
            const pt = this._pano.calculateMousePosition();
            const arrowPosition = new ArrowPosition();
            arrowPosition.xAxis = pt.x;
            arrowPosition.yAxis = pt.y;
            return {
                arrowPosition,
                view: this._activePanorama,
            };
        };
        this.getAverageFov = () => this._panorama.getAverageFov();
        this.getMaxFov = () => this._panorama.getMaxFov();
        this.getMinFov = () => this._panorama.getMinFov();
        this.getCameraPosition = () => {
            const fov = this._pano.getZoom();
            const xAxis = this._pano.getFloatValue('view.hlookat');
            const yAxis = this._pano.getFloatValue('view.vlookat');
            const sceneIndex = this._activePanorama.sceneIndex;
            const tourSceneGuid = this._activePanorama.tourSceneGuid;
            const cameraPosition = new CameraPosition(xAxis, yAxis, fov, sceneIndex, tourSceneGuid);
            return cameraPosition;
        };
        this.dispose = () => {
            this._onDisposeListeners.forEach((listener) => listener());
            this._panorama.dispose();
        };
        this.registerOnClick = (handler) => this._panorama.registerOnClick(handler);
        this.unregisterOnClick = () => this._panorama.unregisterOnClick();
        this._isDifferentArrowType = (x, y) => {
            const areSame = (isSimpleArrow(x) && isSimpleArrow(y)) ||
                (isPolygonalArrow(x) && isPolygonalArrow(y)) ||
                (isTeleportArrow(x) && isTeleportArrow(y));
            return !areSame;
        };
        this.renderPolygonalArrow = (arrow, arrowIdentifier, renderOptions) => {
            if (!this.isOnCurrentView(arrow.sceneIndex, arrow.tourSceneGuid)) {
                return;
            }
            const hotspotIdInPanorama = arrowIdentifier !== null && arrowIdentifier !== void 0 ? arrowIdentifier : EDITED_ARROW_NAME;
            if (arrow.arrowPositionConfiguration.arrowPosition.indicatorType === IndicatorType.Invisible ||
                arrow.arrowPositionConfiguration.arrowPosition.indicatorType === IndicatorType.PolygonalShape) {
                this._pano.removePolygonalArrowIndicator(hotspotIdInPanorama);
            }
            this._panorama.renderPolygonalHotspot(this._hotspotFactory(hotspotIdInPanorama, arrow.arrowPositionConfiguration.arrowPosition.arrowArea, renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.onClick, arrow.arrowPositionConfiguration.arrowPosition.indicatorType, arrow.arrowPositionConfiguration.arrowPosition.indicatorScale, arrow.arrowPositionConfiguration.arrowPosition.indicatorPosition));
            const hotspot = this._pano.getHotspot(hotspotIdInPanorama);
            if (hotspot) {
                if (renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.modifyHotspot) {
                    renderOptions.modifyHotspot(hotspot);
                }
                if (renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.alpha) {
                    hotspot.alpha = renderOptions.alpha;
                }
            }
        };
        this.isOnCurrentView = (sceneIndex, tourSceneGuid) => {
            return isLocatedOnPanoramaActiveView(this, sceneIndex, tourSceneGuid);
        };
        this.getActiveTourSceneGuidView = () => {
            return this._activePanorama.tourSceneGuid;
        };
        this.getActiveSceneIndex = () => {
            return this._activePanorama.sceneIndex;
        };
        this.subscribeVirtualTourChanged = (listener) => {
            this._panoramaChangedListeners.add(listener);
            return () => {
                this._panoramaChangedListeners.delete(listener);
            };
        };
        this.emitVirtualTourChanged = () => {
            this._panoramaChangedListeners.forEach((l) => l());
        };
        this.lookToScene = (tourSceneGuid, keepLookAt) => {
            const tourScene = this._allPanoramas.get(tourSceneGuid);
            // TODO when default camera position for scenes is implemented, use it here.
            const cameraView = keepLookAt ? this._pano.getCurrentViewLookAt() : undefined;
            const position = new CameraPosition(cameraView === null || cameraView === void 0 ? void 0 : cameraView.x, cameraView === null || cameraView === void 0 ? void 0 : cameraView.y, undefined, tourScene === null || tourScene === void 0 ? void 0 : tourScene.startingSceneIndex, tourSceneGuid);
            this.lookToCameraPosition(position, true).catch((e) => {
                throw e;
            });
        };
        this.addLinkBetweenTourScenes = (tourSceneLinkGuid, link) => {
            const arrowId = FriendlyNameService.getFriendlyName(tourSceneLinkGuid);
            this.renderArrowToPano(link, arrowId, {
                onClick: () => {
                    this.lookToScene(link.targetSceneGuid, true);
                },
            });
        };
        this.removeLinkById = (tourSceneLinkGuid) => {
            const arrowId = FriendlyNameService.getFriendlyName(tourSceneLinkGuid);
            this.removeArrow(arrowId);
        };
        this.updateLink = (tourSceneLinkGuid, link) => {
            const arrowId = FriendlyNameService.getFriendlyName(tourSceneLinkGuid);
            this.renderArrowToPano(link, arrowId, {
                onClick: () => {
                    this.lookToScene(link.targetSceneGuid, true);
                },
            });
        };
        this.getArrows = () => this._arrows.asReadonly;
        this.renderArrowsWhenSceneIsChanged = (panorama) => {
            this._arrows.asReadonly.forEach((registryItem, arrowId) => {
                panorama.renderArrowToPano(registryItem.arrow, arrowId, registryItem.renderOptions, Math.random() * 500);
            });
        };
        this.loadPanorama = async (newPanoramaToLoad) => {
            return newPanoramaToLoad
                ? await this._pano.loadPanoFromXmlString(newPanoramaToLoad)
                : await this._pano.loadEmptyPano();
        };
        const onSceneChanged = (panorama) => {
            this._activePanorama = {
                ...this._activePanorama,
                sceneIndex: panorama._pano.getSceneIndex(),
            };
            this.renderArrowsWhenSceneIsChanged(panorama); //have to redraw arrows always on change
            this.emitVirtualTourChanged();
        };
        this._pano = _panorama._pano;
        _panorama.registerOnSceneChange(() => onSceneChanged(this));
    }
    static async Create(tour, viewerId, skinXmlPath, hotspotFactory, embedpanoFunc, view, targetId) {
        var _a, _b, _c, _d, _e;
        const panorama = view
            ? tour === null || tour === void 0 ? void 0 : tour.tourScenes.find((tourScene) => tourScene.guid === (view === null || view === void 0 ? void 0 : view.tourSceneGuid))
            : tour === null || tour === void 0 ? void 0 : tour.tourScenes[0];
        const readyPanorama = await Panorama.CreatePanorama(skinXmlPath, viewerId, embedpanoFunc, panorama === null || panorama === void 0 ? void 0 : panorama.tourScenePanorama, targetId, view === null || view === void 0 ? void 0 : view.sceneIndex);
        let allPanos = Map();
        allPanos = allPanos.withMutations((map) => {
            tour === null || tour === void 0 ? void 0 : tour.tourScenes.forEach((tourScene) => {
                map.set(tourScene.guid, tourScene.tourScenePanorama);
            });
        });
        return new PanoramaV2(hotspotFactory, readyPanorama, {
            tourSceneGuid: (_b = (_a = view === null || view === void 0 ? void 0 : view.tourSceneGuid) !== null && _a !== void 0 ? _a : panorama === null || panorama === void 0 ? void 0 : panorama.guid) !== null && _b !== void 0 ? _b : EMPTY_GUID,
            sceneIndex: (_e = (_c = view === null || view === void 0 ? void 0 : view.sceneIndex) !== null && _c !== void 0 ? _c : (_d = panorama === null || panorama === void 0 ? void 0 : panorama.tourScenePanorama) === null || _d === void 0 ? void 0 : _d.startingSceneIndex) !== null && _e !== void 0 ? _e : 0,
        }, allPanos);
    }
    displayIncorrectAnswerIndicator(text, position) {
        const incorrectLayerName = FriendlyNameService.getFriendlyName('incorrectAnswerLayer');
        const incorrectHotspotName = 'incorrectAnswerHotspot';
        this.removeLayer(incorrectLayerName);
        this._panorama.removeHotspot(incorrectHotspotName);
        const mousePosition = position !== null && position !== void 0 ? position : this._pano.calculateMousePosition();
        this._panorama.addSimpleHotspot(text, mousePosition.x, mousePosition.y, incorrectLayerName, incorrectHotspotName, undefined, 'htext-testme__wrong', LayerStyle.LayerTextTestMe, 0.5, false);
    }
    displayCorrectAnswerIndicator(text, position) {
        const correctLayerName = 'correctAnswerLayer';
        const correctHotspotName = 'correctAnswerHotspot';
        this._panorama.removeLayer('incorrectAnswerLayer');
        this._panorama.removeHotspot('incorrectAnswerHotspot');
        const mousePosition = position !== null && position !== void 0 ? position : this._pano.calculateMousePosition();
        this._panorama.addSimpleHotspot(text, mousePosition.x, mousePosition.y, correctLayerName, correctHotspotName, undefined, 'htext-testme__correct', LayerStyle.LayerTextTestMe, 0.5, false);
    }
    renderSinglePointHotspot(arrow, arrowIdentifier, renderOptions, delayedRenderMs) {
        if (!this.isOnCurrentView(arrow.sceneIndex, arrow.tourSceneGuid)) {
            return;
        }
        const arrowPosition = arrow.arrowPositionConfiguration.arrowPosition;
        const style = arrow.arrowText ? HotspotStyle.HotspotWithText : HotspotStyle.HotspotIcon;
        // Order of the calls is important here, if 'loadStyle' is called after 'visible'
        // is set then hotspot is not displayed
        const hotspot = this._panorama._pano.addHotspot(arrowIdentifier);
        hotspot.loadstyle(style);
        hotspot.ath = arrowPosition.xAxis;
        hotspot.atv = arrowPosition.yAxis + 0.1;
        hotspot.visible = true;
        hotspot.enabled = true;
        hotspot.alpha = 0;
        hotspot.scale = 0;
        hotspot.scalechildren = false;
        setTimeout(() => {
            this._panorama._pano.call(`tween(hotspot[${hotspot.name}].alpha, ${(renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.alpha) ? renderOptions.alpha : 1}, 0.3, linear);`);
            this._panorama._pano.call(`tween(hotspot[${hotspot.name}].scale, 0.5, 0.3, easeoutback);`);
            this._panorama._pano.call(`tween(hotspot[${hotspot.name}].atv, ${arrowPosition.yAxis}, 0.3, easeoutback);`);
        }, delayedRenderMs);
        if (arrow.arrowText) {
            const layerName = this._panorama._getSimpleArrowLayerName(arrowIdentifier);
            const arrowTextLayer = this._panorama._pano.addLayer(layerName);
            arrowTextLayer.loadstyle(LayerStyle.LayerText);
            arrowTextLayer.parent = `hotspot[${arrowIdentifier}]`;
            arrowTextLayer.handcursor = true;
            arrowTextLayer.html = `<p class='htext'>${he.encode(arrow.arrowText)}<p>`;
        }
        if (renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.onClick) {
            hotspot.onclick = renderOptions.onClick;
        }
        if (renderOptions === null || renderOptions === void 0 ? void 0 : renderOptions.modifyHotspot) {
            renderOptions.modifyHotspot(hotspot);
        }
    }
}
